// Iter 82.7 / Phase E2.3 — Regularization Report.
// Funnel of regularization requests with by_status / by_type aggregations,
// approval-time KPI, top requesters, CSV + XLSX exports, drill-down.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DownloadSimple, FileXls, FunnelSimple, ArrowSquareOut } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";

const STATUS_TONE = {
    Draft: "bg-slate-100 text-slate-700",
    Pending: "bg-amber-100 text-amber-800",
    Approved: "bg-emerald-100 text-emerald-800",
    Rejected: "bg-rose-100 text-rose-800",
    Withdrawn: "bg-zinc-100 text-zinc-700",
};

function thirtyDaysAgo() {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
}
function todayIso() { return new Date().toISOString().slice(0, 10); }

export default function AttendanceRegularizationReport() {
    const [filters, setFilters] = useState({
        from_date: thirtyDaysAgo(),
        to_date: todayIso(),
        status_filter: "",
        request_type: "",
        department: "",
    });
    const [data, setData] = useState({ items: [], summary: {} });
    const [loading, setLoading] = useState(true);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
            const { data: payload } = await api.get("/attendance/report/regularization", { params });
            setData(payload);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load regularization report"));
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => { reload(); }, [reload]);

    const download = async (ext) => {
        try {
            const params = {};
            Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
            const { data: blob } = await api.get(`/attendance/report/regularization/export.${ext}`, { params, responseType: "blob" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = `attendance-regularization-${todayIso()}.${ext}`; a.click();
            URL.revokeObjectURL(url);
        } catch (err) { toast.error(formatApiError(err, `${ext.toUpperCase()} export failed`)); }
    };

    const s = data.summary || {};

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1600px]" data-testid="att-reg-page">
            <PageHeader
                eyebrow="Attendance Reports"
                title="Regularization Report"
                description={`${data.total || 0} request${data.total === 1 ? "" : "s"} · avg approval ${(s.avg_approval_days ?? 0).toFixed(1)} day${(s.avg_approval_days ?? 0) === 1 ? "" : "s"}`}
                actions={(
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => download("csv")} data-testid="att-reg-csv-btn">
                            <DownloadSimple size={14} className="mr-1" /> CSV
                        </Button>
                        <Button size="sm" onClick={() => download("xlsx")} data-testid="att-reg-xlsx-btn">
                            <FileXls size={14} className="mr-1" /> XLSX
                        </Button>
                    </div>
                )}
            />

            <section className="mt-5 bg-white rounded-xl border border-slate-200 p-4" data-testid="att-reg-filters">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
                    <div>
                        <Label className="text-[10px] uppercase text-slate-500 font-semibold">From Date</Label>
                        <Input type="date" value={filters.from_date} onChange={(e) => setFilters({ ...filters, from_date: e.target.value })} data-testid="reg-from" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase text-slate-500 font-semibold">To Date</Label>
                        <Input type="date" value={filters.to_date} onChange={(e) => setFilters({ ...filters, to_date: e.target.value })} data-testid="reg-to" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase text-slate-500 font-semibold">Status</Label>
                        <select className="w-full border rounded-md text-sm h-9 px-2" value={filters.status_filter}
                                onChange={(e) => setFilters({ ...filters, status_filter: e.target.value })} data-testid="reg-status">
                            <option value="">All</option>
                            <option value="Draft">Draft</option>
                            <option value="Pending">Pending</option>
                            <option value="Approved">Approved</option>
                            <option value="Rejected">Rejected</option>
                            <option value="Withdrawn">Withdrawn</option>
                        </select>
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase text-slate-500 font-semibold">Type</Label>
                        <select className="w-full border rounded-md text-sm h-9 px-2" value={filters.request_type}
                                onChange={(e) => setFilters({ ...filters, request_type: e.target.value })} data-testid="reg-type">
                            <option value="">All</option>
                            <option value="missing_check_in">Missing Check-In</option>
                            <option value="missing_check_out">Missing Check-Out</option>
                            <option value="missing_break_end">Missing Break End</option>
                            <option value="late_arrival_excuse">Late Arrival Excuse</option>
                            <option value="early_departure_excuse">Early Departure Excuse</option>
                            <option value="full_day_attendance">Full Day Attendance</option>
                            <option value="break_correction">Break Correction</option>
                        </select>
                    </div>
                    <div className="flex items-end col-span-2 lg:col-span-1">
                        <Button variant="outline" size="sm" className="w-full"
                                onClick={() => setFilters({ from_date: thirtyDaysAgo(), to_date: todayIso(), status_filter: "", request_type: "", department: "" })}
                                data-testid="reg-clear">
                            <FunnelSimple size={14} className="mr-1" /> Clear
                        </Button>
                    </div>
                </div>
            </section>

            <section className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="att-reg-kpis">
                <Kpi label="Total" value={s.total || 0} tone="slate" testid="reg-kpi-total" />
                <Kpi label="Avg Approval" value={(s.avg_approval_days ?? 0).toFixed(1)} suffix="d" tone="sky" testid="reg-kpi-avg" />
                <Kpi label="Longest Pending" value={(s.longest_pending_days ?? 0).toFixed(1)} suffix="d" tone="amber" testid="reg-kpi-longest" />
                <Kpi label="Pending" value={(s.by_status || {}).Pending || 0} tone="amber" testid="reg-kpi-pending" />
            </section>

            <section className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="reg-by-status">
                    <h4 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">By Status</h4>
                    {Object.keys(s.by_status || {}).length === 0 ? <div className="text-xs text-slate-400 py-3">No data</div> : (
                        <ul className="space-y-1">
                            {Object.entries(s.by_status || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                                <li key={k} className="flex justify-between text-xs items-center">
                                    <Badge className={`text-[10px] ${STATUS_TONE[k] || "bg-slate-100"}`}>{k}</Badge>
                                    <span className="font-mono font-semibold">{v}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="reg-by-type">
                    <h4 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">By Request Type</h4>
                    {Object.keys(s.by_type || {}).length === 0 ? <div className="text-xs text-slate-400 py-3">No data</div> : (
                        <ul className="space-y-1">
                            {Object.entries(s.by_type || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                                <li key={k} className="flex justify-between text-xs"><span className="text-slate-700">{k.replaceAll("_", " ")}</span><span className="font-mono font-semibold">{v}</span></li>
                            ))}
                        </ul>
                    )}
                </div>
            </section>

            <section className="mt-4 bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="att-reg-table">
                {loading ? (
                    <div className="text-center py-8 text-sm text-slate-400">Loading…</div>
                ) : data.items.length === 0 ? (
                    <div className="text-center py-12 text-sm text-slate-400">No regularization requests match the filters.</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600 text-xs">
                            <tr>
                                <th className="text-left px-3 py-2 font-medium">Date</th>
                                <th className="text-left px-3 py-2 font-medium">Status</th>
                                <th className="text-left px-3 py-2 font-medium">Type</th>
                                <th className="text-left px-3 py-2 font-medium">Employee</th>
                                <th className="text-left px-3 py-2 font-medium">Department</th>
                                <th className="text-left px-3 py-2 font-medium">Submitted</th>
                                <th className="text-right px-3 py-2 font-medium">Age (d)</th>
                                <th className="text-left px-3 py-2 font-medium">Drill</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.items.slice(0, 200).map((r) => (
                                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`reg-row-${r.id}`}>
                                    <td className="px-3 py-2 text-xs font-mono">{r.attendance_date}</td>
                                    <td className="px-3 py-2"><Badge className={`text-xs ${STATUS_TONE[r.status] || "bg-slate-100"}`}>{r.status}</Badge></td>
                                    <td className="px-3 py-2 text-xs">{r.request_type.replaceAll("_", " ")}</td>
                                    <td className="px-3 py-2">
                                        <div className="text-sm font-semibold">{r.employee_name}</div>
                                        <div className="text-xs text-slate-500 font-mono">{r.employee_code}</div>
                                    </td>
                                    <td className="px-3 py-2 text-xs">{r.department || "—"}</td>
                                    <td className="px-3 py-2 text-xs text-slate-500">{r.submitted_at ? r.submitted_at.slice(0, 10) : "—"}</td>
                                    <td className="px-3 py-2 text-right">{r.age_days.toFixed(1)}</td>
                                    <td className="px-3 py-2">
                                        <Link to="/regularization/requests?scope=all" className="text-xs text-sky-700 hover:underline flex items-center gap-1" data-testid={`reg-drill-${r.id}`}>
                                            <ArrowSquareOut size={12} /> Open
                                        </Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>
        </div>
    );
}

function Kpi({ label, value, suffix = "", tone = "slate", testid }) {
    const map = {
        slate: "bg-slate-50 border-slate-200 text-slate-700",
        amber: "bg-amber-50 border-amber-200 text-amber-900",
        sky: "bg-sky-50 border-sky-200 text-sky-900",
    };
    return (
        <div className={`rounded-lg border p-3 ${map[tone]}`} data-testid={testid}>
            <div className="text-[9px] uppercase tracking-wider font-semibold">{label}</div>
            <div className="text-xl font-bold mt-1">{value}{suffix && <span className="text-xs font-normal opacity-70 ml-0.5">{suffix}</span>}</div>
        </div>
    );
}
